import React, { useState, useEffect } from 'react';
import './PredictionPage.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
const GCP_PRD_API_URL = process.env.PRD_API_URL;

// Event descriptions mapping
const EVENT_DESCRIPTIONS = {
  "edsas01": "Attended grade 1 in school",
  "edsas08": "Attend grade 8 in school",
  "edsas12": "Attend grade 12 in school",
  "edhafdu": "Attend full-time, degree unknown",
  "secvavc": "Statistically: A (violent) crime might happen*",
  "edhefdu": "Enroll in full-time education",
  "edhemsd": "Enroll with masters degree",
  "edhebsa": "Enroll with a BA or BSc",
  "edhebeg": "Enroll with a BE",
  "edhebba": "Enroll with a Bachelor of Business",
  "edhephd": "Enroll with a PhD",
  "edheedu": "Enroll with a degree",
  "edhepro": "Enroll with a Professional degree",
  "edhgmsd": "Graduate with Masters degree",
  "edhgbsa": "Graduate with a BA or BSc",
  "edhgbeg": "Graduate with a Bachelor Eng",
  "edhgbba": "Graduate with a Bachelor of Business",
  "edhgphd": "Graduate with a doctoral degree, phd",
  "edhgedu": "Graduate with a degree",
  "edhgpro": "Graduate with a professional degree",
  "crrsjnj": "Start a new job",
  "crrprjb": "Promotion in job",
  "crrlqjb": "Change job",
  "crrrtjb": "Retire from job",
  "prsbapr": "Buy an apartment or a house",
  "prssafp": "Buy an apartment or a house with partner",
  "socpwfd": "Go on a date with someone",
  "socpmrr": "Get married",
  "socpdvd": "Get a divorce",
  "socbprg": "Get pregnant",
  "socbbpr": "Your partner get pregnant",
  "socnb01": "A New born first child",
  "hthchgn": "Might have a chronic health condition",
  "hthchiv": "Beware of HIV/aids",
  "hthcstd": "Get an STD",
  "hthcanm": "Get anemia",
  "hthcazm": "Get asthma",
  "hthccnr": "Get cancer",
  "hthcdbt": "Get diabetes",
  "hthcepl": "Get epilepsy",
  "hthchrt": "Get heart condition",
  "prslrfc": "Relocate to another country",
  "prslctr": "Relocate within Country",
  "prslstt": "Relocate within State",
  "prslctn": "Relocate within the county"
};

// Event vocabulary organized by category
const EVENT_VOCABULARY = [
  {
    category: "EDUCATION",
    events: [
      { code: "edsas08", label: "Finished Middle School" },
      { code: "edsas12", label: "Finished High School" },
      { code: "edhebba", label: "Enroll in BBA" },
      { code: "edhgbba", label: "Graduated BBA" },
      { code: "edhebeg", label: "Enrolled in B.E." },
      { code: "edhgbeg", label: "Graduated B.E." },
      { code: "edhebsa", label: "Enroll in BA/BSc" },
      { code: "edhgbsa", label: "Graduate BA/BSc" },
      { code: "edhemsd", label: "Enroll in Masters" },
      { code: "edhgmsd", label: "Graduate Masters" },
      { code: "edhephd", label: "Enroll in PhD" },
      { code: "edhgphd", label: "Graduate PhD" },
      { code: "edhepro", label: "Enroll Professional Degree" },
      { code: "edhgpro", label: "Graduate Professional Degree" },
      { code: "edheedu", label: "Enroll in Degree" },
      { code: "edhgedu", label: "Graduate with Degree" }
    ]
  },
  {
    category: "CAREER",
    events: [
      { code: "crrsjnj", label: "Start new job" },
      { code: "crrprjb", label: "Promotion" },
      { code: "crrlqjb", label: "Leave the job" },
      { code: "crrrtjb", label: "Retire from job" }
    ]
  },
  {
    category: "FAMILY",
    events: [
      { code: "socpwfd", label: "Go on a date" },
      { code: "socpmrr", label: "Marriage" },
      { code: "socnb01", label: "Birth of Child" },
      { code: "socpdvd", label: "Got divorced" },
      { code: "socbprg", label: "Get pregnant" },
      { code: "socbbpr", label: "Partner get pregnant" }
    ]
  },
  {
    category: "HEALTH",
    events: [
      { code: "hthchgn", label: "A medical condition" },
      { code: "hthcazm", label: "Asthma" },
      { code: "hthcdbt", label: "Diabetes" },
      { code: "hthchrt", label: "Heart condition" },
      { code: "hthccnr", label: "Cancer" }
    ]
  },
  {
    category: "LOCATION",
    events: [
      { code: "prsbapr", label: "Own apartment/house" },
      { code: "prssafp", label: "Own apartment/house with partner" },
      { code: "prslctr", label: "Relocate within country" },
      { code: "prslstt", label: "Relocate within State" },
      { code: "prslctn", label: "Relocates within county" },
      { code: "prslrfc", label: "Relocate to another country" }
    ]
  }
];

const AdSensePlaceholder = ({ position }) => (
  <div className="adsense-placeholder">
    <div className="ad-content">
      <span className="ad-label">Advertisement</span>
      <p className="ad-text">AdSense Ad #{position}</p>
    </div>
  </div>
);

const PredictionPage = ({ user, token, onUpgradeClick }) => {
  const [selectedPersona, setSelectedPersona] = useState('self');
  const [eventSequence, setEventSequence] = useState(['', '', '', '', '', '']);
  const [prediction, setPrediction] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [personas, setPersonas] = useState([]);

  const isFree = user.tier === 'free';
  const maxSequences = user.tier === 'free' ? 4 : user.tier === 'paid' ? 6 : 8;
  const remainingPredictions = getRemainingPredictions();

  useEffect(() => {
    fetchPersonas();
  }, []);

  function getRemainingPredictions() {
    const limits = {
      free: 5,
      paid: 15,
      premium: 25
    };
    return limits[user.tier] - (user.dailyPredictionCount || 0);
  }

  const fetchPersonas = async () => {
    try {
      const response = await fetch(`${API_URL}/personas`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setPersonas(data);
      }
    } catch (err) {
      console.error('Error fetching personas:', err);
    }
  };

  const handleEventChange = (index, value) => {
    const newSequence = [...eventSequence];
    newSequence[index] = value;
    setEventSequence(newSequence);
  };

  const handlePredict = async () => {
    setError('');
    setPrediction(null);

    const filledEvents = eventSequence.filter(e => e !== '').slice(0, maxSequences);
    if (filledEvents.length < 2) {
      setError('Please select at least 2 events for prediction');
      return;
    }

    if (remainingPredictions <= 0) {
      setError('Daily prediction limit reached. Please upgrade or try again tomorrow!');
      onUpgradeClick();
      return;
    }

    setLoading(true);

    try {
      // Call the external prediction API directly
      //const predictionApiUrl = 'http://127.0.0.1:5000/predict';
      //const predictionApiUrl = 'http://192.168.1.32:5000/predict';

//      const predictionApiUrl = '${GCP_PRD_API_URL}/predict';
//      await fetch(predictionApiUrl, {
      const predictionResponse = await fetch('https://prediction-api-302792922407.us-central1.run.app/predict', {
//      const predictionResponse = await fetch(`${GCP_PRD_API_URL}/predict`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          event_sequence: filledEvents
        })
      });

      if (!predictionResponse.ok) {
        throw new Error('Prediction API failed');
      }

      const predictionData = await predictionResponse.json();

      if (predictionData.status === 'success') {
        // Now update our backend to track the prediction count
        await fetch(`${API_URL}/predict`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            personaId: selectedPersona,
            sequenceEvents: filledEvents
          })
        });

        setPrediction(predictionData.prediction_summary);
      } else {
        throw new Error(predictionData.error || 'Prediction failed');
      }
    } catch (err) {
      setError('Network error. Please ensure the prediction API is running at http://127.0.0.1:5000');
      console.error('Prediction error:', err);
    } finally {
      setLoading(false);
    }
  };

  const getEventDescription = (eventCode) => {
    return EVENT_DESCRIPTIONS[eventCode] || eventCode;
  };

  return (
    <div className="prediction-page">
      <div className="cosmic-bg"></div>
      
      <div className="page-header">
        <h1 className="page-title">✨ Your Future Awaits ✨</h1>
        <p className="page-subtitle">Discover what the universe has in store for you</p>
      </div>

      <div className="content-grid">
        {/* Left Column - Main Content */}
        <div className="main-content">
          {/* Persona Selector */}
          <div className="card persona-selector-card">
            <label className="input-label">
              <span className="label-icon">👤</span>
              Choose Persona for Prediction
            </label>
            <select 
              className="persona-select"
              value={selectedPersona}
              onChange={(e) => setSelectedPersona(e.target.value)}
            >
              <option value="self">Myself</option>
              {personas.map(persona => (
                <option key={persona.id} value={persona.id}>
                  {persona.name}
                </option>
              ))}
            </select>
            <div className="prediction-counter">
              <span className="counter-icon">🔮</span>
              <span>{remainingPredictions} predictions remaining today</span>
            </div>
          </div>

          {/* Event Sequence Builder */}
          <div className="card sequence-card">
            <h2 className="card-title">
              <span className="title-icon">📝</span>
              Build Your Event Sequence
            </h2>
            <p className="card-subtitle">Select life events in chronological order (oldest to most recent)</p>

            <div className="event-slots">
              {eventSequence.slice(0, maxSequences).map((event, index) => (
                <div key={index} className="event-slot">
                  <div className="slot-number">{index + 1}</div>
                  <select
                    className="event-select"
                    value={event}
                    onChange={(e) => handleEventChange(index, e.target.value)}
                    disabled={isFree && index >= 4}
                  >
                    <option value="">-- Select an event --</option>
                    {EVENT_VOCABULARY.map(category => (
                      <optgroup key={category.category} label={category.category}>
                        {category.events.map(evt => (
                          <option key={evt.code} value={evt.code}>
                            {evt.label}
                          </option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                  {isFree && index >= 4 && (
                    <div className="locked-overlay" onClick={onUpgradeClick}>
                      <span className="lock-icon">🔒</span>
                      <span>Upgrade to unlock</span>
                    </div>
                  )}
                </div>
              ))}
            </div>

            <button 
              className="predict-btn"
              onClick={handlePredict}
              disabled={loading || remainingPredictions <= 0}
            >
              {loading ? (
                <>
                  <span className="spinner-small"></span>
                  <span>Consulting the cosmos...</span>
                </>
              ) : (
                <>
                  <span>✨ Get My Prediction ✨</span>
                </>
              )}
            </button>

            {isFree && (
              <button className="upgrade-hint-btn" onClick={onUpgradeClick}>
                <span className="hint-icon">💡</span>
                <span>Upgrade to add up to 6 events for more accurate predictions</span>
              </button>
            )}

            {error && (
              <div className="error-message">
                <span className="error-icon">⚠️</span>
                {error}
              </div>
            )}
          </div>

          {/* AdSense Placeholder 1 (Free tier only) */}
          {isFree && <AdSensePlaceholder position={1} />}

          {/* Prediction Result */}
          {prediction && (
            <div className="card prediction-result">
              <h2 className="result-title">
                <span className="result-icon">🔮</span>
                Your Prediction
              </h2>
              <div className="prediction-content">
                {/* Top Prediction */}
                <div className="top-prediction-box">
                  <div className="prediction-label">Most Likely Next Event:</div>
                  <h3 className="top-prediction-event">
                    {getEventDescription(prediction.top_prediction.event)}
                  </h3>
                  <div className="confidence-display">
                    <span className="confidence-label">Confidence:</span>
                    <span className="confidence-value">
                      {(prediction.top_prediction.probability * 100).toFixed(2)}%
                    </span>
                  </div>
                  <div className="time-estimate">
                    <span className="time-label">Estimated Time:</span>
                    <span className="time-value">
                      {prediction.estimated_time.years > 0 
                        ? `${prediction.estimated_time.years} Year${prediction.estimated_time.years > 1 ? 's' : ''} and ${prediction.estimated_time.months.toFixed(1)} Months`
                        : prediction.estimated_time.months < 1
                          ? 'Soon (Less than a month!)'
                          : `${prediction.estimated_time.months.toFixed(1)} Months`
                      }
                    </span>
                  </div>
                </div>

                {/* Other Possibilities */}
                <div className="other-predictions">
                  <h4 className="other-title">Other Possibilities:</h4>
                  <ul className="prediction-list">
                    {prediction.top_events.map((item, index) => (
                      <li key={index} className="prediction-item">
                        <span className="event-name">
                          {getEventDescription(item.event)}
                        </span>
                        <span className="event-probability">
                          {(item.probability * 100).toFixed(2)}%
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Right Column - AdSense */}
        {isFree && (
          <div className="sidebar">
            <AdSensePlaceholder position={2} />
          </div>
        )}
      </div>
    </div>
  );
};

export default PredictionPage;